import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useConvexAuth } from "convex/react";
import { usePostHog } from "posthog-js/react";
import { Loader2, Link2Off, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@mcpjam/design-system/button";
import { ChatTabV2 } from "@/components/ChatTabV2";
import type { ServerWithName } from "@/hooks/use-app-state";
import { useApiContext } from "@/hooks/hosted/use-hosted-api-context";
import { useHostedOAuthGate } from "@/hooks/hosted/use-hosted-oauth-gate";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { authFetch } from "@/lib/session-token";
import {
  buildChatboxLink,
  clearChatboxSession,
  extractChatboxTokenFromPath,
  normalizeChatboxSession,
  readPlaygroundSession,
  readChatboxSurfaceFromUrl,
  readChatboxSession,
  CHATBOX_OAUTH_PENDING_KEY,
  chatboxEnabledOptionalStorageKey,
  slugify,
  type ChatboxSession,
  writeChatboxSession,
  writeChatboxSignInReturnPath,
} from "@/lib/chatbox-session";
import { navigateApp } from "@/lib/app-navigation";
import {
  isEmbeddedPreview,
  syncChatboxBootstrapHash,
  syncChatboxSessionHash,
} from "@/lib/embedded-preview";
import { bootstrapServerToHostedOAuthDescriptor } from "@/components/chatboxes/builder/chatbox-server-optional";
import { isHostedOAuthBusy } from "@/lib/hosted-oauth-resume";
import type { HostedOAuthRequiredDetails } from "@/lib/hosted-oauth-required";
import {
  ChatboxChatUiOverrideProvider,
  ChatboxHostStyleProvider,
} from "@/contexts/chatbox-client-style-context";
import { ChatboxHostCapabilitiesOverrideProvider } from "@/contexts/chatbox-client-capabilities-override-context";
import { ActiveMcpProfileProvider } from "@/contexts/active-mcp-profile-context";
import { ActiveHostCapsResolverScope } from "@/contexts/active-host-client-capabilities-context";
import { ChatboxSurfaceProvider } from "@/contexts/chatbox-surface-context";
import { WebManagedServersProvider } from "@/contexts/web-managed-servers-context";
import { ChatboxHostOnboardingOverlays } from "@/components/hosted/ChatboxHostOnboardingOverlays";
import { useChatboxHostIntroGate } from "@/components/hosted/useChatboxHostIntroGate";
import { getChatboxShellStyle } from "@/lib/chatbox-client-style";

interface ChatboxChatPageProps {
  pathToken?: string | null;
  onExitChatboxChat?: () => void;
}

interface ChatboxRouteError {
  status: number;
  code?: string;
  message: string;
  rawMessage: string;
}

type ChatboxErrorKind =
  | "access_denied"
  | "guest_blocked"
  | "invalid_link"
  | "playground_expired"
  | "unexpected";

interface ChatboxDisplayError {
  kind: ChatboxErrorKind;
  title: string;
  message: string;
}

const INVALID_CHATBOX_LINK_MESSAGE =
  "This chatbox link is invalid or expired. Ask the owner to share a new link if you still need access.";
const UNEXPECTED_CHATBOX_ERROR_MESSAGE =
  "We couldn't open this chatbox right now. Please try again or open MCPJam.";

type ChatboxBootstrapAuthMode = "workos" | "guest";
type ChatboxLandingState =
  | "resolvingAuth"
  | "bootstrapping"
  | "ready"
  | "denied";

function sanitizeChatboxRouteErrorMessage(message: string): string {
  const normalized = message.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "";
  }

  const withoutWrapper = normalized.replace(/^Uncaught Error:\s*/i, "");
  return withoutWrapper
    .replace(/\s+at\s+(?:async\s+)?[A-Za-z0-9_$./<>-]+(?:\s+\(|$).*/s, "")
    .trim();
}

function createChatboxRouteError(
  status: number,
  message: string,
  code?: string
): ChatboxRouteError {
  const fallbackMessage = `Request failed with status ${status}`;
  const rawMessage = message.trim() || fallbackMessage;
  const sanitizedMessage = sanitizeChatboxRouteErrorMessage(rawMessage);

  return {
    status,
    code,
    rawMessage,
    message: sanitizedMessage || fallbackMessage,
  };
}

async function readRouteError(response: Response): Promise<ChatboxRouteError> {
  const bodyText = await response.text();
  const trimmedBody = bodyText.trim();
  let code: string | undefined;
  let message = trimmedBody;

  try {
    const body = (trimmedBody ? JSON.parse(trimmedBody) : null) as {
      code?: string;
      message?: string;
      error?: string;
    } | null;

    code = typeof body?.code === "string" ? body.code : undefined;
    message =
      body?.message ||
      body?.error ||
      trimmedBody ||
      `Request failed with status ${response.status}`;
  } catch {
    message = trimmedBody || `Request failed with status ${response.status}`;
  }

  return createChatboxRouteError(response.status, message, code);
}

function isChatboxRouteError(error: unknown): error is ChatboxRouteError {
  return (
    !!error &&
    typeof error === "object" &&
    "status" in error &&
    typeof error.status === "number" &&
    "message" in error &&
    typeof error.message === "string" &&
    "rawMessage" in error &&
    typeof error.rawMessage === "string"
  );
}

function getChatboxDisplayError(
  error: ChatboxRouteError | null
): ChatboxDisplayError {
  if (!error) {
    return {
      kind: "invalid_link",
      title: "Chatbox Link Unavailable",
      message: INVALID_CHATBOX_LINK_MESSAGE,
    };
  }

  const normalizedMessage = error.message.toLowerCase();
  const requiresSignIn = normalizedMessage.includes(
    "sign in to access this chatbox"
  );
  const isAccessDenied = normalizedMessage.includes("don't have access");
  const isGuestBlocked =
    normalizedMessage.includes("guests cannot access") ||
    normalizedMessage.includes("guest access");
  const isInvalidLink =
    error.status === 404 ||
    error.code === "NOT_FOUND" ||
    normalizedMessage.includes("invalid or has expired") ||
    normalizedMessage.includes("invalid or expired");
  const isPlaygroundExpired = normalizedMessage.includes(
    "playground session expired"
  );

  if (isPlaygroundExpired) {
    return {
      kind: "playground_expired",
      title: "Preview unavailable",
      message: error.message,
    };
  }

  if (requiresSignIn || isAccessDenied) {
    return {
      kind: "access_denied",
      title: "Access Denied",
      message: error.message,
    };
  }

  if (isGuestBlocked) {
    return {
      kind: "guest_blocked",
      title: "Access Denied",
      message: error.message,
    };
  }

  if (isInvalidLink) {
    return {
      kind: "invalid_link",
      title: "Chatbox Link Unavailable",
      message: INVALID_CHATBOX_LINK_MESSAGE,
    };
  }

  return {
    kind: "unexpected",
    title: "Chatbox Link Unavailable",
    message: UNEXPECTED_CHATBOX_ERROR_MESSAGE,
  };
}

function getChatboxBootstrapAuthMode(
  isAuthenticated: boolean
): ChatboxBootstrapAuthMode {
  return isAuthenticated ? "workos" : "guest";
}

function isInteractiveSignInRequired(kind: ChatboxErrorKind): boolean {
  return kind === "access_denied" || kind === "guest_blocked";
}

export function ChatboxChatPage({
  pathToken,
  onExitChatboxChat,
}: ChatboxChatPageProps) {
  const {
    getAccessToken,
    signIn,
    user: workOsUser,
    isLoading: isWorkOsLoading,
  } = useAuth();
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  const themeMode = usePreferencesStore((s) => s.themeMode);

  useEffect(() => {
    posthogRef.current = posthog;
  }, [posthog]);

  const playgroundParams = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const isPlayground = params.get("playground") === "1";
      const playgroundId = params.get("playgroundId");
      return isPlayground && playgroundId ? { playgroundId } : null;
    } catch {
      return null;
    }
  }, []);

  // The embedded Preview iframe is same-origin, so it shares the tab's
  // sessionStorage with the outer dashboard. Reading or writing the chatbox
  // session from inside the embed would leak it into (or pick it up from)
  // the host app — the outer App treats a stored session as "render the
  // chatbox runtime", hijacking the dashboard on the next reload. The embed
  // never needs the fallback anyway: its URL keeps the share token (the
  // post-redeem strip only runs standalone), so a reload re-redeems.
  const readCurrentSession = useCallback(() => {
    if (playgroundParams) {
      return readPlaygroundSession(playgroundParams.playgroundId);
    }

    return isEmbeddedPreview() ? null : readChatboxSession();
  }, [playgroundParams]);

  const writeCurrentSession = useCallback(
    (nextSession: ChatboxSession) => {
      if (playgroundParams || isEmbeddedPreview()) {
        return;
      }

      writeChatboxSession(nextSession);
    },
    [playgroundParams]
  );

  const clearCurrentSession = useCallback(() => {
    if (playgroundParams || isEmbeddedPreview()) {
      return;
    }

    clearChatboxSession();
  }, [playgroundParams]);

  const [session, setSession] = useState<ChatboxSession | null>(() =>
    readCurrentSession()
  );
  const [isBootstrapping, setIsBootstrapping] = useState(
    Boolean(pathToken || playgroundParams)
  );
  const [routeError, setRouteError] = useState<ChatboxRouteError | null>(null);
  const interactiveSignInEventKeyRef = useRef<string | null>(null);
  const tokenFromPath = useMemo(() => pathToken?.trim() || null, [pathToken]);
  // Mirror `tokenFromPath` into a ref so async work (the silent re-redeem
  // below) can detect a mid-flight navigation: when the user switches
  // chatbox tokens before the in-flight `/api/web/chatboxes/redeem`
  // response arrives, the resolved-but-stale session must not overwrite
  // the new token's active session.
  const tokenFromPathRef = useRef(tokenFromPath);
  useEffect(() => {
    tokenFromPathRef.current = tokenFromPath;
  }, [tokenFromPath]);
  const isAuthSettling =
    Boolean(tokenFromPath) &&
    !playgroundParams &&
    (isWorkOsLoading || isAuthLoading);

  const sessionServersRequired = useMemo(
    () => session?.payload.servers.filter((s) => !s.optional) ?? [],
    [session]
  );

  const sessionServersOptional = useMemo(
    () => session?.payload.servers.filter((s) => s.optional) ?? [],
    [session]
  );

  const [enabledOptionalServerIds, setEnabledOptionalServerIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (!session?.chatboxId) return;
    try {
      const raw = sessionStorage.getItem(
        chatboxEnabledOptionalStorageKey(session.chatboxId)
      );
      if (!raw) {
        setEnabledOptionalServerIds((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return;
      const optionalIdSet = new Set(
        session.payload.servers.filter((s) => s.optional).map((s) => s.serverId)
      );
      const next = parsed.filter(
        (id): id is string => typeof id === "string" && optionalIdSet.has(id)
      );
      setEnabledOptionalServerIds((prev) => {
        if (
          prev.length === next.length &&
          prev.every((id, i) => id === next[i])
        ) {
          return prev;
        }
        return next;
      });
    } catch {
      setEnabledOptionalServerIds((prev) => (prev.length === 0 ? prev : []));
    }
    // Intentionally only re-hydrate when the chatbox id changes — not when
    // `payload.servers` gets a new array identity on each render.
  }, [session?.chatboxId]);

  useEffect(() => {
    if (!session?.chatboxId) return;
    try {
      const key = chatboxEnabledOptionalStorageKey(session.chatboxId);
      const serialized = JSON.stringify(enabledOptionalServerIds);
      if (sessionStorage.getItem(key) === serialized) return;
      sessionStorage.setItem(key, serialized);
    } catch {
      // ignore
    }
  }, [session?.chatboxId, enabledOptionalServerIds]);

  const sessionServersActive = useMemo(() => {
    if (!session) return [];
    const enabled = new Set(enabledOptionalServerIds);
    const optionalActive = session.payload.servers.filter(
      (s) => s.optional && enabled.has(s.serverId)
    );
    return [...sessionServersRequired, ...optionalActive];
  }, [session, sessionServersRequired, enabledOptionalServerIds]);

  const oauthServers = useMemo(
    () => sessionServersActive.map(bootstrapServerToHostedOAuthDescriptor),
    [sessionServersActive]
  );

  const handleEnableChatboxOptionalServer = useCallback((serverId: string) => {
    setEnabledOptionalServerIds((prev) =>
      prev.includes(serverId) ? prev : [...prev, serverId]
    );
  }, []);

  const chatboxOptionalInventory = useMemo(() => {
    const enabled = new Set(enabledOptionalServerIds);
    return sessionServersOptional
      .filter((s) => !enabled.has(s.serverId))
      .map((s) => ({
        serverId: s.serverId,
        serverName: s.serverName,
        useOAuth: s.useOAuth,
      }));
  }, [sessionServersOptional, enabledOptionalServerIds]);
  const {
    pendingOAuthServers,
    authorizeServer,
    markOAuthRequired,
    hasBusyOAuth,
  } = useHostedOAuthGate({
    surface: "chatbox",
    pendingKey: CHATBOX_OAUTH_PENDING_KEY,
    servers: oauthServers,
    projectId: session?.payload.projectId ?? null,
    chatboxId: session?.chatboxId,
    accessVersion: session?.accessVersion,
    isAuthenticated,
  });

  const chatboxServerConfigs = useMemo(() => {
    if (!session) return {};

    return Object.fromEntries(
      sessionServersActive.map((server) => [
        server.serverName,
        {
          name: server.serverName,
          config: {
            url: "https://chatbox-chat.invalid",
          } as any,
          lastConnectionTime: new Date(),
          connectionStatus: "connected",
          retryCount: 0,
          enabled: true,
        } satisfies ServerWithName,
      ])
    );
  }, [session, sessionServersActive]);

  const hostedServerIdsByName = useMemo(() => {
    if (!session) return {};

    return Object.fromEntries(
      sessionServersActive.flatMap((server) => [
        [server.serverName, server.serverId],
        [server.serverId, server.serverId],
      ])
    );
  }, [session, sessionServersActive]);

  useApiContext({
    projectId: session?.payload.projectId ?? null,
    serverIdsByName: session ? hostedServerIdsByName : {},
    getAccessToken,
    // Resolved chatbox identity from /api/web/chatboxes/redeem. Both
    // fields live at the top level of the session — the URL token is
    // never threaded onto the read path.
    chatboxId: session?.chatboxId,
    accessVersion: session?.accessVersion,
    isAuthenticated: !!workOsUser,
    hasSession: !!workOsUser || isWorkOsLoading,
  });

  useEffect(() => {
    if (isAuthSettling) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      if (playgroundParams) {
        const snapshot = readPlaygroundSession(playgroundParams.playgroundId);
        if (snapshot) {
          setSession({ ...snapshot, surface: "preview" });
          setRouteError(null);
        } else {
          setSession(null);
          setRouteError(
            createChatboxRouteError(
              410,
              "Playground session expired. Return to the builder to preview."
            )
          );
        }
        setIsBootstrapping(false);
        return;
      }

      if (tokenFromPath) {
        const authMode = getChatboxBootstrapAuthMode(isAuthenticated);
        setIsBootstrapping(true);
        setRouteError(null);
        posthogRef.current.capture("chatbox_bootstrap_started", {
          surface: "chatbox",
          auth_mode: authMode,
          status: "started",
        });
        try {
          // /redeem exchanges the URL link token for a `chatboxId` +
          // `accessVersion` grant plus the bootstrap payload, in one round
          // trip. Every chatbox-aware backend call then keys on the resolved
          // identity — the URL token is not threaded onto the read path.
          const redeemResponse = await authFetch("/api/web/chatboxes/redeem", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ chatboxToken: tokenFromPath }),
          });

          if (!redeemResponse.ok) {
            throw await readRouteError(redeemResponse);
          }

          const redeemed = (await redeemResponse.json()) as {
            chatboxId?: unknown;
            accessVersion?: unknown;
            bootstrap?: unknown;
          };
          if (cancelled) return;

          // The redeem response is treated as untrusted shape until validated
          // — `normalizeChatboxSession` enforces every field
          // `ChatboxBootstrapPayload` requires. Without this, a partial
          // bootstrap (missing projectId/modelId/etc.) would be persisted
          // and the API context downstream would initialize with `null`s.
          const nextSession = normalizeChatboxSession({
            chatboxId:
              typeof redeemed.chatboxId === "string"
                ? redeemed.chatboxId
                : undefined,
            accessVersion:
              typeof redeemed.accessVersion === "number"
                ? redeemed.accessVersion
                : undefined,
            payload: redeemed.bootstrap as
              | ChatboxSession["payload"]
              | undefined,
            surface: readChatboxSurfaceFromUrl(window.location.search),
            shareToken: tokenFromPath ?? undefined,
          });
          if (!nextSession) {
            throw createChatboxRouteError(
              502,
              "Chatbox redeem returned an incomplete bootstrap payload."
            );
          }

          writeCurrentSession(nextSession);
          setSession(nextSession);
          setRouteError(null);

          syncChatboxBootstrapHash(slugify(nextSession.payload.name));
          posthogRef.current.capture("chatbox_bootstrap_silent_success", {
            surface: "chatbox",
            auth_mode: authMode,
            status: "success",
          });
        } catch (error) {
          if (cancelled) return;
          setSession(null);
          clearCurrentSession();

          const nextError = isChatboxRouteError(error)
            ? error
            : createChatboxRouteError(
                500,
                error instanceof Error
                  ? error.message
                  : "Unable to open this chatbox."
              );
          const displayError = getChatboxDisplayError(nextError);

          if (displayError.kind === "unexpected") {
            console.error("[ChatboxChatPage] Failed to bootstrap chatbox", {
              status: nextError.status,
              code: nextError.code,
              message: nextError.message,
              rawMessage: nextError.rawMessage,
            });
          }

          setRouteError(nextError);
          posthogRef.current.capture("chatbox_bootstrap_silent_failure", {
            surface: "chatbox",
            auth_mode: authMode,
            status: "failure",
            error_kind: displayError.kind,
            http_status: nextError.status,
          });
        } finally {
          if (!cancelled) {
            setIsBootstrapping(false);
          }
        }
        return;
      }

      const recovered = readCurrentSession();
      if (recovered) {
        setSession(recovered);
        setRouteError(null);
        syncChatboxBootstrapHash(slugify(recovered.payload.name));
        return;
      }

      setSession(null);
      setRouteError(
        createChatboxRouteError(404, "Invalid or expired chatbox link")
      );
    };

    void resolve();

    return () => {
      cancelled = true;
    };
  }, [
    clearCurrentSession,
    isAuthenticated,
    isAuthSettling,
    playgroundParams,
    readCurrentSession,
    tokenFromPath,
    writeCurrentSession,
  ]);

  // Silent re-redeem path. The capture hook (or any chatbox-aware caller)
  // calls this when the backend reports `chatbox_access_stale`. It re-runs
  // /web/chatbox/redeem against the URL token and updates `session` in
  // place, which propagates a fresh `accessVersion` to every downstream
  // consumer. Errors are swallowed — the original error UI is owned by the
  // primary bootstrap effect above, and a refresh failure should leave the
  // already-mounted chat alone rather than tearing the UI down.
  //
  // The in-flight latch is keyed by *token*, not a plain boolean. A
  // navigation that swaps `tokenFromPath` from A to B while A's redeem is
  // still pending must not block B from starting its own refresh — A's
  // response would be discarded by the token-staleness guards below
  // anyway, so leaving B with no refresh in flight would strand the
  // capture hook's queued stale snapshot until an unrelated stale event
  // or page reload happens to retrigger the callback.
  const refreshInFlightTokenRef = useRef<string | null>(null);
  const requestRefreshAccessVersion = useCallback(() => {
    const token = tokenFromPath;
    if (!token) return;
    // Same-token re-entry → drop. Different-token re-entry → allow; the
    // older fetch will land in storage but its `setSession` is gated by
    // `tokenFromPathRef`.
    if (refreshInFlightTokenRef.current === token) return;
    refreshInFlightTokenRef.current = token;
    void (async () => {
      try {
        const redeemResponse = await authFetch("/api/web/chatboxes/redeem", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ chatboxToken: token }),
        });
        if (!redeemResponse.ok) return;
        if (tokenFromPathRef.current !== token) return;
        const redeemed = (await redeemResponse.json()) as {
          chatboxId?: unknown;
          accessVersion?: unknown;
          bootstrap?: unknown;
        };
        if (tokenFromPathRef.current !== token) return;
        const nextSession = normalizeChatboxSession({
          chatboxId:
            typeof redeemed.chatboxId === "string"
              ? redeemed.chatboxId
              : undefined,
          accessVersion:
            typeof redeemed.accessVersion === "number"
              ? redeemed.accessVersion
              : undefined,
          payload: redeemed.bootstrap as ChatboxSession["payload"] | undefined,
          surface: readChatboxSurfaceFromUrl(window.location.search),
          shareToken: token,
        });
        if (!nextSession) return;
        // Final guard before mutating shared session state: a navigation
        // between the JSON parse and now would still race.
        if (tokenFromPathRef.current !== token) return;
        writeCurrentSession(nextSession);
        setSession(nextSession);
      } catch (error) {
        console.warn(
          "[ChatboxChatPage] Silent chatbox re-redeem failed",
          error
        );
      } finally {
        // Only clear the latch if we're still the active in-flight refresh.
        // A newer token's refresh may have already overwritten it; don't
        // stomp on that one.
        if (refreshInFlightTokenRef.current === token) {
          refreshInFlightTokenRef.current = null;
        }
      }
    })();
  }, [tokenFromPath, writeCurrentSession]);

  const displayError = useMemo(
    () => getChatboxDisplayError(routeError),
    [routeError]
  );
  const landingState: ChatboxLandingState = isAuthSettling
    ? "resolvingAuth"
    : isBootstrapping
    ? "bootstrapping"
    : session
    ? "ready"
    : "denied";

  useEffect(() => {
    if (
      landingState !== "denied" ||
      isAuthenticated ||
      !isInteractiveSignInRequired(displayError.kind)
    ) {
      interactiveSignInEventKeyRef.current = null;
      return;
    }

    const authMode = getChatboxBootstrapAuthMode(isAuthenticated);
    const eventKey = `${displayError.kind}:${authMode}:${
      routeError?.status ?? 0
    }`;
    if (interactiveSignInEventKeyRef.current === eventKey) {
      return;
    }

    interactiveSignInEventKeyRef.current = eventKey;
    posthogRef.current.capture("interactive_signin_required", {
      surface: "chatbox",
      auth_mode: authMode,
      status: "required",
      error_kind: displayError.kind,
      http_status: routeError?.status,
    });
  }, [displayError.kind, isAuthenticated, landingState, routeError?.status]);

  useEffect(() => {
    if (!session) return;

    const expectedHash = slugify(session.payload.name);
    const enforceHash = () => {
      syncChatboxSessionHash(expectedHash);
    };

    enforceHash();
    window.addEventListener("hashchange", enforceHash);
    return () => {
      window.removeEventListener("hashchange", enforceHash);
    };
  }, [session]);

  const shareableToken = tokenFromPath ?? session?.shareToken?.trim() ?? null;

  const handleCopyLink = useCallback(async () => {
    // Token preference: live URL → persisted `session.shareToken`. After
    // redeem we strip the token from the address bar via replaceState,
    // so `session.shareToken` (captured at redeem time) is what makes
    // Copy link work across reloads.
    const token = shareableToken;
    if (!session || !token) {
      toast.error("Chatbox link unavailable");
      return;
    }

    if (!navigator.clipboard?.writeText) {
      toast.error("Copy is not available in this browser");
      return;
    }

    try {
      await navigator.clipboard.writeText(
        buildChatboxLink(token, session.payload.name)
      );
      toast.success("Chatbox link copied");
    } catch {
      toast.error("Failed to copy chatbox link");
    }
  }, [session, shareableToken]);

  const handleOpenMcpJam = useCallback(() => {
    clearChatboxSession();
    // Route via the navigation API so React Router's `useLocation`
    // (consumed by App's pathname-sync effect) sees the new pathname.
    // A bare `window.history.replaceState` would leave `locationForRoute`
    // stale on `/chatbox/...`, and the sync effect would then redirect
    // back to `/servers` before the hash-migration shim could pivot.
    navigateApp("/chatboxes", {
      replace: isEmbeddedPreview() ? true : true,
    });
    onExitChatboxChat?.();
  }, [onExitChatboxChat]);

  const handleSignIn = useCallback(() => {
    writeChatboxSignInReturnPath(window.location.pathname);
    signIn();
  }, [signIn]);

  const handleOAuthRequired = useCallback(
    (details?: HostedOAuthRequiredDetails) => {
      markOAuthRequired(details);
    },
    [markOAuthRequired]
  );

  const hostStyle = session?.payload.hostStyle ?? "claude";
  const chatUiOverride = session?.payload.chatUiOverride;
  const shellStyle = getChatboxShellStyle(hostStyle, themeMode, chatUiOverride);
  const oauthPending = pendingOAuthServers.length > 0;
  const welcomeAvailable =
    (session?.payload.chatUi?.surfaces?.welcome?.enabled ?? true) &&
    !!session?.payload.chatUi?.surfaces?.welcome?.body?.trim();
  const introGate = useChatboxHostIntroGate({
    chatboxId: session?.payload.chatboxId ?? "",
    servers: sessionServersRequired,
    oauthPending,
    hasBusyOAuth,
    pendingOAuthServers,
    welcomeAvailable,
  });
  const isFinishingOAuth =
    pendingOAuthServers.length > 0 &&
    pendingOAuthServers.every(({ state }) => isHostedOAuthBusy(state.status));

  const renderContent = () => {
    if (landingState === "resolvingAuth" || landingState === "bootstrapping") {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (landingState === "denied") {
      const isAccessDenied = displayError.kind === "access_denied";
      const guestBlocked = displayError.kind === "guest_blocked";

      return (
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
            <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              {isAccessDenied || guestBlocked ? (
                <ShieldX className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Link2Off className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <h2 className="text-base font-semibold text-foreground">
              {displayError.title}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {displayError.message}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              {!isAuthenticated && (isAccessDenied || guestBlocked) ? (
                <Button onClick={handleSignIn}>Sign in</Button>
              ) : null}
              <Button variant="outline" onClick={handleOpenMcpJam}>
                Open in App
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (!session) {
      return null;
    }

    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatTabV2
          connectedOrConnectingServerConfigs={chatboxServerConfigs}
          selectedServerNames={sessionServersActive.map(
            (server) => server.serverName
          )}
          minimalMode
          reasoningDisplayMode="hidden"
          hostedContext={{
            chatboxId: session.chatboxId,
            accessVersion: session.accessVersion,
            chatboxSurface: session.surface ?? "share_link",
            projectId: session.payload.projectId,
            selectedServerIds: sessionServersActive.map(
              (server) => server.serverId
            ),
            requestRefreshAccessVersion,
            // Redeemed sessions carry Convex-resolved server ids; only the
            // web chat engine can connect them. Playground previews keep
            // the platform default (local engine + builder connections).
            requiresWebChatApi: !playgroundParams,
          }}
          executionConfig={{
            modelId: session.payload.modelId,
            systemPrompt: session.payload.systemPrompt,
            temperature: session.payload.temperature,
            requireToolApproval: session.payload.requireToolApproval,
          }}
          onOAuthRequired={handleOAuthRequired}
          chatboxComposerBlocked={introGate.composerBlocked}
          chatboxComposerBlockedReason="Get started or authorize to send messages…"
          chatboxOptionalInventory={chatboxOptionalInventory}
          onEnableChatboxOptionalServer={handleEnableChatboxOptionalServer}
        />
        <ChatboxHostOnboardingOverlays
          showWelcome={introGate.showWelcome}
          onGetStarted={introGate.dismissIntro}
          welcomeBody={session.payload.chatUi?.surfaces?.welcome?.body}
          showAuthPanel={introGate.showAuthPanel}
          pendingOAuthServers={pendingOAuthServers}
          authorizeServer={authorizeServer}
          isFinishingOAuth={isFinishingOAuth}
        />
      </div>
    );
  };

  return (
    <ChatboxHostStyleProvider value={hostStyle}>
      <ChatboxChatUiOverrideProvider value={chatUiOverride}>
        <ChatboxHostCapabilitiesOverrideProvider
          value={session?.payload.hostCapabilitiesOverride}
        >
          <ActiveMcpProfileProvider value={session?.payload.mcpProfile}>
            {/*
        Hosted bootstrap payload doesn't (yet) carry clientCapabilities —
        we pass `activeHost={null}` and let the scope fall back to the
        template seed for `hostStyle`. Correct for unmodified host styles;
        if a chatbox owner customizes capabilities, that will require a
        bootstrap-payload extension (out of scope here).
      */}
            <ActiveHostCapsResolverScope
              activeHost={null}
              hostStyle={hostStyle}
            >
              <ChatboxSurfaceProvider value={true}>
                {/* Redeemed sessions: servers are Convex-resolved, so MCP
                    Apps widget fetches and bridge resource/prompt calls
                    must take the hosted API branch on every platform.
                    Playground previews keep platform routing (local
                    builds reuse the builder's local connections). */}
                <WebManagedServersProvider value={!playgroundParams}>
                <div
                  className="chatbox-host-shell flex h-svh min-h-0 flex-col overflow-hidden"
                  data-host-style={hostStyle}
                  style={shellStyle}
                >
                  <header className="border-b border-border/50 bg-background/95 backdrop-blur">
                    <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-2.5">
                      <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                        {session?.payload.name || "\u00A0"}
                      </h1>
                      <button
                        onClick={handleOpenMcpJam}
                        className="cursor-pointer flex-shrink-0 border-none bg-transparent p-0"
                      >
                        <img
                          src={
                            themeMode === "dark"
                              ? "/mcp_jam_dark.png"
                              : "/mcp_jam_light.png"
                          }
                          alt="MCPJam"
                          className="h-4 w-auto object-contain"
                        />
                      </button>
                      <div className="flex flex-1 items-center justify-end gap-1.5">
                        {session && shareableToken ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={handleCopyLink}
                          >
                            Copy link
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </header>

                  {renderContent()}
                </div>
                </WebManagedServersProvider>
              </ChatboxSurfaceProvider>
            </ActiveHostCapsResolverScope>
          </ActiveMcpProfileProvider>
        </ChatboxHostCapabilitiesOverrideProvider>
      </ChatboxChatUiOverrideProvider>
    </ChatboxHostStyleProvider>
  );
}

export function getChatboxPathTokenFromLocation(): string | null {
  return extractChatboxTokenFromPath(window.location.pathname);
}
